/**
 * `<caos-action-bar>` — primary and secondary actions for a surface, with an overflow
 * affordance. (Catalog: Actions › ActionBar — primary and secondary actions for a surface, with an overflow affordance.)
 *
 * A horizontal bar that composes the AF1 `caos-button` and the AF4 `caos-overflow-menu`:
 * a `start` slot for context on the left (a title, a selection count), the default slot
 * for the action controls on the right, and an `overflow` slot that holds a
 * `caos-overflow-menu` for the actions that collapse behind a single trigger when the
 * surface is narrow. The bar wraps rather than ever forcing horizontal overflow, and the
 * action cluster stays right-aligned. Purely presentational — it lays out and aligns the
 * controls the caller supplies; which actions live inline vs. behind the overflow is the
 * assembled surface's decision. Styling seams: `::part(bar)`, `::part(start)`,
 * `::part(actions)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';

const sheet = makeSheet(`
  :host { display: block; }

  .bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--caos-space-3);
    padding: var(--caos-space-2) 0;
    max-width: 100%;
  }
  /* a bordered, surface-filled variant for a docked bar */
  :host([framed]) .bar {
    padding: var(--caos-space-2) var(--caos-space-3);
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
  }

  .start {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    min-width: 0;
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
  .start:not(:has(*)) { display: none; }

  .actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--caos-space-2);
    /* push the action cluster to the trailing edge */
    margin-left: auto;
  }
  .actions:not(:has(*)) { display: none; }
`);

export class CaosActionBar extends CaosElement {
  /** CAOS-648 — the toolbar's name follows the host's `aria-label` whenever it changes. */
  static get observedAttributes(): string[] {
    return ['aria-label'];
  }

  private readonly bar: HTMLDivElement;

  constructor() {
    super(sheet);

    const bar = document.createElement('div');
    this.bar = bar;
    bar.className = 'bar';
    bar.setAttribute('part', 'bar');
    bar.setAttribute('role', 'toolbar');

    const start = document.createElement('div');
    start.className = 'start';
    start.setAttribute('part', 'start');
    const startSlot = document.createElement('slot');
    startSlot.name = 'start';
    start.append(startSlot);

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    // default slot = the inline action controls; the `overflow` slot trails them
    const defaultSlot = document.createElement('slot');
    const overflowSlot = document.createElement('slot');
    overflowSlot.name = 'overflow';
    actions.append(defaultSlot, overflowSlot);

    bar.append(start, actions);
    this.root.append(bar);

    this.syncName();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'aria-label') this.syncName();
  }

  private syncName(): void {
    applyAccessibleName(this.bar, this.getAttribute('aria-label'), 'Actions');
  }
}

define('caos-action-bar', CaosActionBar);

declare global {
  interface HTMLElementTagNameMap {
    'caos-action-bar': CaosActionBar;
  }
}
